/* Business-in-a-Box catalog — single source of truth for the 100
 * /business/[slug] hub pages + /browse-businesses.
 *
 * Source: "Apex Flow Digital — The Final SOP" §2 (the locked 100-business
 * list, 18 categories) + "Final SOP v3 — Crush & Sell" §5 (4-tier
 * ladder + Crush layer) + "Website Domination" §9.2 (hub page spec).
 *
 * Locked decisions (Sagar, 2026-06-10):
 *   - Bundle ladder: $99 Starter / $249 Professional / $499 Agency /
 *     $999 Ultimate. Agency + Ultimate are Apex Flow Digital exclusive.
 *   - Apex Pass: $99/yr, 30% off digital (Starter and Professional kits).
 *   - Doctrine: no fake stats, no fabricated testimonials. Founder
 *     notes below are category-level positioning copy grounded in
 *     Brian's real 13 years operating Spiker Carpet & Tile Care —
 *     never invented customer claims. */

import { YEARS_OPERATING } from "./catalog-counts";

export type SpikerBase = "cleaning" | "contracting" | "home-reno" | "general";
export type AuthorityWeight = "high" | "medium" | "low";

export type BusinessCategory = {
  slug: string;
  label: string;
  /* Short positioning line for the category card on /browse-businesses */
  tagline: string;
  /* Which of Brian's real operating playbooks anchors this category */
  spikerBase: SpikerBase;
  /* How directly Brian's 13 years map onto this category (SOP §6.1) */
  authorityWeight: AuthorityWeight;
  /* Category-level founder note — grounded in the real Spiker story,
   * rendered in the "founder note" callout on every hub page in the
   * category. Written as positioning copy, not a fabricated quote. */
  founderNote: string;
};

export type Business = {
  slug: string;
  name: string;
  categorySlug: string;
  /* One-sentence card / meta blurb — what this bundle lets you do */
  blurb: string;
  /* "for X" persona phrase used in headlines and FAQ interpolation */
  persona: string;
};

/* ── Business-in-a-Box kit ladder (per business) ─────────────────── */

export const BUNDLE_STARTER_USD = 99;
export const BUNDLE_PROFESSIONAL_USD = 249;
export const BUNDLE_AGENCY_USD = 499;
export const BUNDLE_ULTIMATE_USD = 999;

/* ── Design bundle ladder (per niche) ─────────────────────────────
 * The four-tier DESIGN pack ladder ($7 / $29 / $69 / $129) — distinct
 * from the Business-in-a-Box kit ladder above ($99–$999). ONE source
 * so the homepage "Specials & Bundles" bullet, its visual mock, and the
 * catalog-loop card can never disagree (SOP item 1.8: one card, one
 * truth). Labels match the homepage mock: Starter · Core · Pro · Mega. */
export const DESIGN_BUNDLE_TIERS = [
  { label: "Starter", priceUsd: 7 },
  { label: "Core", priceUsd: 29 },
  { label: "Pro", priceUsd: 69 },
  { label: "Mega", priceUsd: 129 },
] as const;

export type BundleTier = {
  key: "starter" | "professional" | "agency" | "ultimate";
  name: string;
  priceUsd: number;
  /* Tiers above Professional never leave Apex Flow Digital (SOP §3.1) */
  adExclusive: boolean;
  pitch: string;
  contents: string[];
};

/* Tier contents per Final SOP v3 §5. `{name}` is interpolated with
 * the business name at render time. */
export const BUNDLE_TIERS: BundleTier[] = [
  {
    key: "starter",
    name: "Starter",
    priceUsd: BUNDLE_STARTER_USD,
    adExclusive: false,
    pitch: "Everything you need to open for business this week.",
    contents: [
      "The closest-match Notion CRM for {name} work",
      "3–5 best Canva marketing templates for the niche",
      "3 wall art designs matched to the business aesthetic",
      "60-second sales walkthrough video",
      "Welcome PDF with a first-7-days checklist",
      "Commercial license — use for your business + client work, ongoing updates",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    priceUsd: BUNDLE_PROFESSIONAL_USD,
    adExclusive: false,
    pitch: "The full operating stack — most buyers pick this one.",
    contents: [
      "Everything in Starter",
      "Full Canva pack (15–25 templates)",
      "Full wall art set (8–10 designs)",
      "3D models where relevant (3–5)",
      "Spiker-derived SOPs for the business type",
      "AI Business Pack — 9 assistants incl. Competitive Intel",
      "Crush-the-Competition playbook (personalized PDF)",
      "15–30 min instructional video + 10–15 min tutorial",
      "21-day playbook PDF + 90-day marketing calendar",
    ],
  },
  {
    key: "agency",
    name: "Agency",
    priceUsd: BUNDLE_AGENCY_USD,
    adExclusive: true,
    pitch: "Run it for clients — license, white-label, done-for-you.",
    contents: [
      "Everything in Professional (commercial license already included)",
      "Reseller + white-label rights — rebrand and resell to clients",
      "Client portal Notion template",
      "Done-for-you marketing (10 social posts + 5 emails)",
      "Multi-user Notion workspace template",
      "Crush playbook licensed for white-label client use",
    ],
  },
  {
    key: "ultimate",
    name: "Ultimate",
    priceUsd: BUNDLE_ULTIMATE_USD,
    adExclusive: true,
    pitch: "Everything in this category — plus AI on top.",
    contents: [
      "Everything in Agency",
      "All future updates in this business category",
      "Cross-business access to related-business assets",
      "Quarterly bonus drops + competitive intel refreshes",
      "Free upgrades to new Apex AI products in the category",
      "Premium AI assistant access (highest tier)",
      "Featured listing in the Apex Operator Directory",
    ],
  },
];

/* ── AI Business Pack (Professional+) — v3 spec: 9 components ────── */

/* `key` = the canonical component slug. It matches the engine-output
 * filename stem (NN-<key>.md) and the slug the AI-staff checkout sends in
 * metadata.roles, so the frontend pick, the generated file, and the
 * backend fulfilment all agree. */
export const AI_BUSINESS_PACK = [
  {
    key: "ai-sales-assistant",
    name: "AI Sales Assistant",
    what: "Pre-trained on {name} sales objections, scripts, and qualifying questions — closes leads via copy-paste workflow.",
  },
  {
    key: "ai-follow-up-assistant",
    name: "AI Follow-Up Assistant",
    what: "Generates personalized follow-up emails and texts from a single context block.",
  },
  {
    key: "ai-review-response-assistant",
    name: "AI Review Response Assistant",
    what: "Drafts review responses — positive, negative, neutral — in your brand voice.",
  },
  {
    key: "ai-marketing-content-generator",
    name: "AI Marketing Content Generator",
    what: "Instagram, Facebook, and TikTok copy, blog ideas, and email subject lines for {name} work.",
  },
  {
    key: "ai-estimate-generator",
    name: "AI Estimate Generator",
    what: "Generates job estimates from customer requirements + scope, trained on real service-business pricing logic.",
  },
  {
    key: "ai-customer-service-assistant",
    name: "AI Customer Service Assistant",
    what: "Handles common customer questions, complaints, and refund requests for the business type.",
  },
  {
    key: "ai-competitive-intel-assistant",
    name: "AI Competitive Intel Assistant",
    what: "Generates ongoing local-competitor positioning reports on demand.",
  },
  {
    key: "gpt-workflow-files",
    name: "GPT Workflow Files",
    what: "Exportable .json files for ChatGPT / Claude custom GPTs, pre-configured for {name}.",
  },
  {
    key: "voice-script-templates",
    name: "Voice Script Templates",
    what: "Pre-written phone screening, appointment confirmation, and follow-up call scripts for voice AI.",
  },
] as const;

/* ── The 18 categories ───────────────────────────────────────────── */

export const BUSINESS_CATEGORIES: BusinessCategory[] = [
  {
    slug: "home-services",
    label: "Home Services",
    tagline: "The trucks-and-trust businesses Brian actually ran.",
    spikerBase: "cleaning",
    authorityWeight: "high",
    founderNote:
      "This is the category I lived for 13 years. Spiker Carpet & Tile Care started with a truck and a phone in Galt, California — the pricing scripts, follow-up sequences, and review-ask process in these bundles are the exact systems that ran the business in the Sacramento Valley.",
  },
  {
    slug: "trades",
    label: "Trades",
    tagline: "Skilled-hands businesses with contractor-grade systems.",
    spikerBase: "contracting",
    authorityWeight: "high",
    founderNote:
      "Trades win on showing up, quoting clean, and stacking reviews. These bundles carry the contracting-side systems from 13 years of real service operations — estimates, job tracking, and the follow-up cadence most trades never build.",
  },
  {
    slug: "real-estate",
    label: "Real Estate",
    tagline: "Pipeline, listings, and follow-up that never leaks.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Real estate is a follow-up business wearing a property costume. The CRM discipline in these bundles is the same operating muscle that kept Spiker's pipeline full for 13 years — applied to listings, buyers, and closings.",
  },
  {
    slug: "wedding",
    label: "Wedding",
    tagline: "High-stakes events run on checklists, not chaos.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "A wedding is a service job where nothing gets a second take. These bundles apply hard-won service-operations discipline — timelines, vendor coordination, client communication — to the one day that has to go right.",
  },
  {
    slug: "photography",
    label: "Photography",
    tagline: "Book more shoots, deliver faster, get rebooked.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Photographers don't lose clients over photos — they lose them over slow replies and messy delivery. These bundles install the operations layer so your craft is the only thing clients remember.",
  },
  {
    slug: "coaching",
    label: "Coaching",
    tagline: "Turn expertise into a booked-out practice.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Coaching businesses die from admin, not from lack of skill. These bundles handle intake, scheduling, client tracking, and content so the coaching hours stay coaching hours.",
  },
  {
    slug: "fitness",
    label: "Fitness",
    tagline: "Clients, programming, and retention in one system.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Retention is the whole game in fitness. The follow-up and check-in systems in these bundles are the same customer-retention mechanics that kept Spiker's clients coming back for 13 years.",
  },
  {
    slug: "beauty",
    label: "Beauty",
    tagline: "Full books, repeat clients, and a brand that holds.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Beauty is a local service business — same physics as carpet care: bookings, reviews, rebookings. These bundles ship the local-domination playbook tuned for chairs and studios instead of trucks.",
  },
  {
    slug: "pet-industry",
    label: "Pet Industry",
    tagline: "Recurring clients who love you (and their humans).",
    spikerBase: "cleaning",
    authorityWeight: "medium",
    founderNote:
      "Pet businesses run on trust and recurring schedules — exactly like the pet-odor side of Spiker's work. These bundles carry the recurring-service systems that turn one booking into a standing appointment.",
  },
  {
    slug: "creator-economy",
    label: "Creator Economy",
    tagline: "Publish on a system, monetize on a schedule.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Creators burn out because everything lives in their head. These bundles move the pipeline — ideas, production, sponsors, publishing — into systems that run whether you feel inspired or not.",
  },
  {
    slug: "local-professional",
    label: "Local Professional",
    tagline: "Practice systems for the trusted-advisor businesses.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Professionals win locally the same way service companies do: visibility, reviews, and follow-through. These bundles apply the local-market playbook to practices instead of trucks.",
  },
  {
    slug: "events",
    label: "Events",
    tagline: "Quotes, logistics, and bookings without the scramble.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Events are service jobs with hard deadlines. These bundles bring quoting, logistics, and client-communication systems from 13 years of scheduled service work into the events world.",
  },
  {
    slug: "auto",
    label: "Auto",
    tagline: "Mobile and shop-based auto businesses, systemized.",
    spikerBase: "cleaning",
    authorityWeight: "high",
    founderNote:
      "Auto services are truck-and-trust businesses — Spiker's home turf. Quoting, scheduling, before/after proof, and the review engine in these bundles map one-to-one from 13 years of mobile service operations.",
  },
  {
    slug: "food-hospitality",
    label: "Food & Hospitality",
    tagline: "From first menu to repeat customers, systemized.",
    spikerBase: "general",
    authorityWeight: "medium",
    founderNote:
      "Food businesses live and die on margins and repeat customers. These bundles ship the costing, ordering, and customer-return systems that most food founders build too late.",
  },
  {
    slug: "childcare-education",
    label: "Childcare & Education",
    tagline: "Schedules, parents, and growth — all under control.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Parents choose providers on trust signals — responsiveness, organization, reviews. These bundles install those signals from day one.",
  },
  {
    slug: "crafting-maker",
    label: "Crafting & Maker",
    tagline: "Turn the craft into a shop that actually sells.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Makers underprice and undermarket. These bundles carry the pricing-strategy and marketing systems so the work gets the margin it deserves.",
  },
  {
    slug: "tech-services",
    label: "Tech Services",
    tagline: "Client work without the feast-or-famine cycle.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "Freelance tech work is a pipeline business. These bundles install lead tracking, proposals, and retainer systems so the next client is lined up before the current project ships.",
  },
  {
    slug: "e-commerce",
    label: "E-Commerce",
    tagline: "Inventory, listings, and margins in one dashboard.",
    spikerBase: "general",
    authorityWeight: "low",
    founderNote:
      "E-commerce rewards operators, not dreamers. These bundles ship the listing, inventory, and margin-tracking systems that separate stores from hobbies.",
  },
];

/* BUSINESS_TOTAL is exported just below the BUSINESSES array (it derives
 * from BUSINESSES.length, so it must come after the array is declared). */

/* ── The locked 100-business list (Final SOP §2) ─────────────────── */

export const BUSINESSES: Business[] = [
  /* HOME SERVICES (12) */
  { slug: "carpet-cleaning", name: "Carpet Cleaning", categorySlug: "home-services", persona: "carpet cleaning operators", blurb: "Brian's own business — quoting, scheduling, review-stacking, and follow-up systems from 13 real years." },
  { slug: "house-cleaning", name: "House Cleaning", categorySlug: "home-services", persona: "house cleaning operators", blurb: "Recurring-client scheduling, cleaning checklists, and the review engine that fills a route." },
  { slug: "window-cleaning", name: "Window Cleaning", categorySlug: "home-services", persona: "window cleaning operators", blurb: "Route planning, quote calculators, and rebooking sequences for residential + storefront work." },
  { slug: "pressure-washing", name: "Pressure Washing", categorySlug: "home-services", persona: "pressure washing operators", blurb: "Before/after marketing systems, job estimates, and seasonal-demand pipelines." },
  { slug: "landscaping", name: "Landscaping", categorySlug: "home-services", persona: "landscapers", blurb: "Crew scheduling, recurring maintenance contracts, and upsell systems for design work." },
  { slug: "pool-service", name: "Pool Service", categorySlug: "home-services", persona: "pool service operators", blurb: "Route management, chemical-log templates, and recurring-billing client systems." },
  { slug: "pest-control", name: "Pest Control", categorySlug: "home-services", persona: "pest control operators", blurb: "Treatment scheduling, compliance records, and the recurring-contract sales system." },
  { slug: "hvac", name: "HVAC", categorySlug: "home-services", persona: "HVAC operators", blurb: "Service-call dispatch, maintenance-plan sales, and estimate templates that close." },
  { slug: "plumbing", name: "Plumbing", categorySlug: "home-services", persona: "plumbers", blurb: "Emergency-call intake, job quoting, and the review velocity system for local dominance." },
  { slug: "electrical", name: "Electrical", categorySlug: "home-services", persona: "electricians", blurb: "Job estimates, permit tracking, and customer-communication templates that win bids." },
  { slug: "handyman", name: "Handyman", categorySlug: "home-services", persona: "handyman operators", blurb: "Multi-trade job tracking, hourly + flat-rate pricing systems, and repeat-client marketing." },
  { slug: "junk-removal", name: "Junk Removal", categorySlug: "home-services", persona: "junk removal operators", blurb: "Volume-based quoting, route scheduling, and the before/after content engine." },

  /* TRADES (5) */
  { slug: "roofing", name: "Roofing", categorySlug: "trades", persona: "roofing contractors", blurb: "Inspection reports, insurance-claim workflows, and high-ticket estimate systems." },
  { slug: "painting", name: "Painting", categorySlug: "trades", persona: "painting contractors", blurb: "Room-by-room quoting, color-consult templates, and crew job tracking." },
  { slug: "flooring", name: "Flooring", categorySlug: "trades", persona: "flooring contractors", blurb: "Material + labor estimates, showroom-style galleries, and project pipelines." },
  { slug: "concrete", name: "Concrete", categorySlug: "trades", persona: "concrete contractors", blurb: "Bid templates, pour scheduling, and the portfolio marketing system for driveways to patios." },
  { slug: "drywall", name: "Drywall", categorySlug: "trades", persona: "drywall contractors", blurb: "Square-footage quoting, builder-relationship CRM, and finish-quality documentation." },

  /* REAL ESTATE (5) */
  { slug: "real-estate-agent", name: "Real Estate Agent", categorySlug: "real-estate", persona: "real estate agents", blurb: "Listing pipeline, buyer CRM, open-house kits, and the follow-up engine that never leaks a lead." },
  { slug: "real-estate-broker", name: "Real Estate Broker", categorySlug: "real-estate", persona: "brokers", blurb: "Agent-team management, transaction tracking, and brokerage-level marketing systems." },
  { slug: "real-estate-investor", name: "Real Estate Investor", categorySlug: "real-estate", persona: "real estate investors", blurb: "Deal-analysis calculators, pipeline tracking, and contractor-coordination systems." },
  { slug: "airbnb-operator", name: "Airbnb Operator", categorySlug: "real-estate", persona: "short-term rental hosts", blurb: "Turnover scheduling, guest-communication templates, and multi-listing dashboards." },
  { slug: "property-manager", name: "Property Manager", categorySlug: "real-estate", persona: "property managers", blurb: "Tenant CRM, maintenance-request tracking, and owner-reporting templates." },

  /* WEDDING (7) */
  { slug: "wedding-planner", name: "Wedding Planner", categorySlug: "wedding", persona: "wedding planners", blurb: "Client CRM, vendor database, day-of timelines, and every Canva asset from save-the-date to program." },
  { slug: "wedding-photographer", name: "Wedding Photographer", categorySlug: "wedding", persona: "wedding photographers", blurb: "Booking pipeline, shot-list systems, gallery delivery, and album upsell workflows." },
  { slug: "wedding-coordinator", name: "Wedding Coordinator", categorySlug: "wedding", persona: "day-of coordinators", blurb: "Minute-by-minute timeline builders, vendor briefs, and emergency-kit checklists." },
  { slug: "wedding-venue", name: "Wedding Venue", categorySlug: "wedding", persona: "venue owners", blurb: "Tour booking, event calendars, vendor-list management, and package pricing systems." },
  { slug: "wedding-florist", name: "Wedding Florist", categorySlug: "wedding", persona: "wedding florists", blurb: "Proposal templates, stem-count calculators, and delivery-day logistics systems." },
  { slug: "wedding-content-creator", name: "Wedding Content Creator", categorySlug: "wedding", persona: "wedding content creators", blurb: "Same-day-edit workflows, client packages, and the social delivery system couples share." },
  { slug: "wedding-decor-rental", name: "Wedding Decor Rental", categorySlug: "wedding", persona: "decor rental owners", blurb: "Inventory tracking, damage-deposit workflows, and booking calendars for rental fleets." },

  /* PHOTOGRAPHY (6) */
  { slug: "family-photographer", name: "Family Photographer", categorySlug: "photography", persona: "family photographers", blurb: "Mini-session systems, seasonal campaigns, and the rebooking engine for annual clients." },
  { slug: "portrait-photographer", name: "Portrait Photographer", categorySlug: "photography", persona: "portrait photographers", blurb: "Session workflows, pricing menus, and gallery-to-print upsell systems." },
  { slug: "commercial-photographer", name: "Commercial Photographer", categorySlug: "photography", persona: "commercial photographers", blurb: "Brand-client proposals, usage-rights pricing, and retainer pipelines." },
  { slug: "real-estate-photographer", name: "Real Estate Photographer", categorySlug: "photography", persona: "real estate photographers", blurb: "Agent-relationship CRM, shoot scheduling, and 24-hour delivery workflows." },
  { slug: "sports-photographer", name: "Sports Photographer", categorySlug: "photography", persona: "sports photographers", blurb: "League and event pipelines, team-gallery sales, and volume order systems." },
  { slug: "pet-photographer", name: "Pet Photographer", categorySlug: "photography", persona: "pet photographers", blurb: "Session prep guides, patience-proof workflows, and the pet-parent referral engine." },

  /* COACHING (5) */
  { slug: "life-coach", name: "Life Coach", categorySlug: "coaching", persona: "life coaches", blurb: "Client intake, session tracking, program templates, and content systems that book discovery calls." },
  { slug: "business-coach", name: "Business Coach", categorySlug: "coaching", persona: "business coaches", blurb: "Client dashboards, framework libraries, and the authority-content engine." },
  { slug: "executive-coach", name: "Executive Coach", categorySlug: "coaching", persona: "executive coaches", blurb: "Engagement tracking, 360-review templates, and corporate-proposal systems." },
  { slug: "relationship-coach", name: "Relationship Coach", categorySlug: "coaching", persona: "relationship coaches", blurb: "Couples intake systems, session frameworks, and discreet client management." },
  { slug: "fitness-coach", name: "Fitness Coach", categorySlug: "coaching", persona: "fitness coaches", blurb: "Client programming, check-in automation, and transformation-story marketing systems." },

  /* FITNESS (5) */
  { slug: "personal-trainer", name: "Personal Trainer", categorySlug: "fitness", persona: "personal trainers", blurb: "Session scheduling, program builders, and the client-retention system that fills your calendar." },
  { slug: "online-fitness-coach", name: "Online Fitness Coach", categorySlug: "fitness", persona: "online fitness coaches", blurb: "Remote client dashboards, check-in workflows, and scalable program delivery." },
  { slug: "gym-owner", name: "Gym Owner", categorySlug: "fitness", persona: "gym owners", blurb: "Membership management, class scheduling, retention campaigns, and staff systems." },
  { slug: "yoga-instructor", name: "Yoga Instructor", categorySlug: "fitness", persona: "yoga instructors", blurb: "Class scheduling, private-session pipelines, and retreat-planning systems." },
  { slug: "nutrition-coach", name: "Nutrition Coach", categorySlug: "fitness", persona: "nutrition coaches", blurb: "Meal-plan templates, client food logs, and progress-tracking dashboards." },

  /* BEAUTY (5) */
  { slug: "hair-stylist", name: "Hair Stylist", categorySlug: "beauty", persona: "hair stylists", blurb: "Booking systems, color-formula records, and the rebooking engine that fills the chair." },
  { slug: "lash-artist", name: "Lash Artist", categorySlug: "beauty", persona: "lash artists", blurb: "Fill scheduling, client records, aftercare templates, and referral systems." },
  { slug: "esthetician", name: "Esthetician", categorySlug: "beauty", persona: "estheticians", blurb: "Treatment records, skincare-plan templates, and retail upsell systems." },
  { slug: "nail-technician", name: "Nail Technician", categorySlug: "beauty", persona: "nail technicians", blurb: "Appointment systems, design portfolios, and the standing-appointment retention engine." },
  { slug: "makeup-artist", name: "Makeup Artist", categorySlug: "beauty", persona: "makeup artists", blurb: "Bridal-trial workflows, kit checklists, event booking, and portfolio marketing systems." },

  /* PET INDUSTRY (5) */
  { slug: "dog-trainer", name: "Dog Trainer", categorySlug: "pet-industry", persona: "dog trainers", blurb: "Program tracking, session notes, owner-homework templates, and package sales systems." },
  { slug: "pet-groomer", name: "Pet Groomer", categorySlug: "pet-industry", persona: "pet groomers", blurb: "Grooming records, recurring scheduling, and the photo-driven referral engine." },
  { slug: "pet-sitter", name: "Pet Sitter", categorySlug: "pet-industry", persona: "pet sitters", blurb: "Booking calendars, visit-report templates, and key-management + trust systems." },
  { slug: "dog-walker", name: "Dog Walker", categorySlug: "pet-industry", persona: "dog walkers", blurb: "Route scheduling, pack management, walk reports, and recurring-billing systems." },
  { slug: "pet-breeder", name: "Pet Breeder", categorySlug: "pet-industry", persona: "ethical breeders", blurb: "Litter records, waitlist management, buyer screening, and health-documentation systems." },

  /* CREATOR ECONOMY (6) */
  { slug: "youtuber", name: "YouTuber", categorySlug: "creator-economy", persona: "YouTubers", blurb: "Content pipelines, thumbnail systems, sponsor CRM, and publishing calendars." },
  { slug: "influencer", name: "Influencer", categorySlug: "creator-economy", persona: "influencers", blurb: "Brand-deal pipelines, rate cards, content calendars, and media-kit templates." },
  { slug: "course-creator", name: "Course Creator", categorySlug: "creator-economy", persona: "course creators", blurb: "Curriculum builders, launch-sequence templates, and student-progress dashboards." },
  { slug: "podcaster", name: "Podcaster", categorySlug: "creator-economy", persona: "podcasters", blurb: "Episode pipelines, guest CRM, sponsor tracking, and repurposing workflows." },
  { slug: "newsletter-creator", name: "Newsletter Creator", categorySlug: "creator-economy", persona: "newsletter writers", blurb: "Editorial calendars, growth-loop systems, sponsor pipelines, and archive management." },
  { slug: "affiliate-marketer", name: "Affiliate Marketer", categorySlug: "creator-economy", persona: "affiliate marketers", blurb: "Offer tracking, content-to-conversion pipelines, and commission dashboards." },

  /* LOCAL PROFESSIONAL (6) */
  { slug: "accountant", name: "Accountant", categorySlug: "local-professional", persona: "accountants", blurb: "Client onboarding, deadline tracking, document collection, and advisory upsell systems." },
  { slug: "bookkeeper", name: "Bookkeeper", categorySlug: "local-professional", persona: "bookkeepers", blurb: "Monthly-close checklists, client portals, and the recurring-retainer sales system." },
  { slug: "tax-professional", name: "Tax Professional", categorySlug: "local-professional", persona: "tax professionals", blurb: "Season pipelines, document checklists, extension tracking, and year-round advisory systems." },
  { slug: "insurance-agent", name: "Insurance Agent", categorySlug: "local-professional", persona: "insurance agents", blurb: "Policy pipelines, renewal automation, referral systems, and cross-sell dashboards." },
  { slug: "attorney", name: "Attorney", categorySlug: "local-professional", persona: "attorneys", blurb: "Matter management, client intake, deadline calendars, and consultation-booking systems." },
  { slug: "mortgage-broker", name: "Mortgage Broker", categorySlug: "local-professional", persona: "mortgage brokers", blurb: "Loan pipelines, document collection, realtor-referral CRM, and rate-watch follow-ups." },

  /* EVENTS (5) */
  { slug: "dj", name: "DJ", categorySlug: "events", persona: "DJs", blurb: "Booking pipelines, event questionnaires, song-request systems, and package pricing." },
  { slug: "event-planner", name: "Event Planner", categorySlug: "events", persona: "event planners", blurb: "Client CRM, vendor databases, run-of-show builders, and budget trackers." },
  { slug: "party-rental", name: "Party Rental", categorySlug: "events", persona: "party rental owners", blurb: "Inventory calendars, delivery logistics, damage-deposit workflows, and quote builders." },
  { slug: "balloon-artist", name: "Balloon Artist", categorySlug: "events", persona: "balloon artists", blurb: "Install quoting, design proposals, event calendars, and the photo-portfolio engine." },
  { slug: "corporate-event-coordinator", name: "Corporate Event Coordinator", categorySlug: "events", persona: "corporate event coordinators", blurb: "Stakeholder management, vendor RFPs, run-of-show systems, and post-event reporting." },

  /* AUTO (4) */
  { slug: "auto-detailing", name: "Auto Detailing", categorySlug: "auto", persona: "auto detailers", blurb: "Package pricing, mobile scheduling, before/after marketing, and membership-plan systems." },
  { slug: "mobile-mechanic", name: "Mobile Mechanic", categorySlug: "auto", persona: "mobile mechanics", blurb: "Service-call intake, parts tracking, digital estimates, and route scheduling." },
  { slug: "car-wash", name: "Car Wash", categorySlug: "auto", persona: "car wash owners", blurb: "Membership programs, shift scheduling, and the recurring-revenue dashboard." },
  { slug: "auto-repair-shop", name: "Auto Repair Shop", categorySlug: "auto", persona: "shop owners", blurb: "Bay scheduling, estimate workflows, parts ordering, and customer-communication systems." },

  /* FOOD / HOSPITALITY (5) */
  { slug: "food-truck", name: "Food Truck", categorySlug: "food-hospitality", persona: "food truck operators", blurb: "Location scheduling, menu costing, event bookings, and social-following systems." },
  { slug: "personal-chef", name: "Personal Chef", categorySlug: "food-hospitality", persona: "personal chefs", blurb: "Client menus, dietary records, booking calendars, and weekly-service retainer systems." },
  { slug: "catering", name: "Catering", categorySlug: "food-hospitality", persona: "caterers", blurb: "Event quoting, menu builders, staffing checklists, and tasting-to-booking pipelines." },
  { slug: "baker", name: "Baker", categorySlug: "food-hospitality", persona: "bakers", blurb: "Custom-order intake, pricing calculators, production schedules, and pickup systems." },
  { slug: "meal-prep-service", name: "Meal Prep Service", categorySlug: "food-hospitality", persona: "meal prep operators", blurb: "Weekly menu planning, subscription management, delivery routes, and macro-tracking templates." },

  /* CHILDCARE / EDUCATION (5) */
  { slug: "tutor", name: "Tutor", categorySlug: "childcare-education", persona: "tutors", blurb: "Student tracking, session scheduling, progress reports, and parent-communication systems." },
  { slug: "daycare", name: "Daycare", categorySlug: "childcare-education", persona: "daycare owners", blurb: "Enrollment pipelines, daily-report templates, staff scheduling, and compliance trackers." },
  { slug: "music-teacher", name: "Music Teacher", categorySlug: "childcare-education", persona: "music teachers", blurb: "Lesson scheduling, practice trackers, recital planning, and studio-policy templates." },
  { slug: "youth-sports-coach", name: "Youth Sports Coach", categorySlug: "childcare-education", persona: "youth sports coaches", blurb: "Roster management, practice plans, parent communication, and season calendars." },
  { slug: "babysitter", name: "Babysitter", categorySlug: "childcare-education", persona: "babysitters", blurb: "Family profiles, booking calendars, rate cards, and trust-building profile templates." },

  /* CRAFTING / MAKER (5) */
  { slug: "etsy-seller", name: "Etsy Seller", categorySlug: "crafting-maker", persona: "Etsy sellers", blurb: "Listing systems, order tracking, pricing calculators, and the review-velocity engine." },
  { slug: "print-on-demand-operator", name: "Print-on-Demand Operator", categorySlug: "crafting-maker", persona: "POD operators", blurb: "Design pipelines, multi-platform listing trackers, and margin dashboards." },
  { slug: "candle-maker", name: "Candle Maker", categorySlug: "crafting-maker", persona: "candle makers", blurb: "Batch records, supply costing, wholesale pipelines, and market-day checklists." },
  { slug: "soap-maker", name: "Soap Maker", categorySlug: "crafting-maker", persona: "soap makers", blurb: "Recipe records, cure-time tracking, labeling compliance, and wholesale systems." },
  { slug: "woodworker", name: "Woodworker", categorySlug: "crafting-maker", persona: "woodworkers", blurb: "Commission intake, project quoting, material tracking, and portfolio marketing." },

  /* TECH SERVICES (4) */
  { slug: "web-designer", name: "Web Designer", categorySlug: "tech-services", persona: "web designers", blurb: "Client pipelines, proposal templates, project trackers, and maintenance-retainer systems." },
  { slug: "social-media-manager", name: "Social Media Manager", categorySlug: "tech-services", persona: "social media managers", blurb: "Client content calendars, approval workflows, reporting templates, and retainer pricing." },
  { slug: "virtual-assistant", name: "Virtual Assistant", categorySlug: "tech-services", persona: "virtual assistants", blurb: "Client onboarding, task tracking, time logs, and the package-pricing system." },
  { slug: "it-consultant", name: "IT Consultant", categorySlug: "tech-services", persona: "IT consultants", blurb: "Ticket tracking, client documentation, proposal templates, and managed-service pipelines." },

  /* E-COMMERCE (5) */
  { slug: "amazon-fba-seller", name: "Amazon FBA Seller", categorySlug: "e-commerce", persona: "FBA sellers", blurb: "Product-research trackers, inventory forecasts, PPC dashboards, and supplier CRMs." },
  { slug: "dropshipper", name: "Dropshipper", categorySlug: "e-commerce", persona: "dropshippers", blurb: "Product-testing pipelines, supplier management, ad trackers, and margin calculators." },
  { slug: "shopify-store-owner", name: "Shopify Store Owner", categorySlug: "e-commerce", persona: "Shopify store owners", blurb: "Store-launch checklists, product pipelines, email-flow templates, and CRO trackers." },
  { slug: "boutique-owner", name: "Boutique Owner", categorySlug: "e-commerce", persona: "boutique owners", blurb: "Inventory buying, lookbook templates, live-sale systems, and customer-loyalty programs." },
  { slug: "print-on-demand-t-shirt-seller", name: "Print-on-Demand T-Shirt Seller", categorySlug: "e-commerce", persona: "POD t-shirt sellers", blurb: "Design-to-listing pipelines, niche research trackers, and multi-marketplace dashboards." },
];

/* Total Business-in-a-Box offerings shown on the site.
 *
 * Gate 0 (Master SOP) — "one catalog number, one source, no fake."
 * Derived from the live BUSINESSES array, NOT a hand-typed number, so the
 * headline can never claim more businesses than actually have hub pages.
 * Today that is 100. When the planned product categories ship as real
 * /business hubs, the array grows and this rises automatically — no copy
 * edits, no drift. (The earlier hard-coded 124 counted 24 not-yet-built
 * product categories as businesses; that was the contradiction Gate 0 flags.) */
export const BUSINESS_TOTAL = BUSINESSES.length;

/* ── Lookup helpers ──────────────────────────────────────────────── */

const BUSINESS_BY_SLUG = new Map(BUSINESSES.map((b) => [b.slug, b]));
const CATEGORY_BY_SLUG = new Map(
  BUSINESS_CATEGORIES.map((c) => [c.slug, c]),
);

export function getBusiness(slug: string): Business | undefined {
  return BUSINESS_BY_SLUG.get(slug);
}

export function getBusinessCategory(
  slug: string,
): BusinessCategory | undefined {
  return CATEGORY_BY_SLUG.get(slug);
}

export function getBusinessesInCategory(categorySlug: string): Business[] {
  return BUSINESSES.filter((b) => b.categorySlug === categorySlug);
}

export function getRelatedBusinesses(business: Business, limit = 5): Business[] {
  return getBusinessesInCategory(business.categorySlug)
    .filter((b) => b.slug !== business.slug)
    .slice(0, limit);
}

/* Interpolate `{name}` placeholders in tier/pack copy. */
export function fillName(template: string, name: string): string {
  return template.replaceAll("{name}", name);
}

/* "Compare buying separately" anchor — conservative, defensible math:
 * a Notion system + full Canva pack + wall art set + SOP library +
 * AI prompt pack bought piecemeal across the catalog. Rendered as
 * "$2,000+" style anchor on hub pages (SOP §7.1 item 3). */
export const BUNDLE_SEPARATE_VALUE_USD = 2_000;

/* ── First-7-days checklist (Welcome PDF preview, SOP §7.1 item 6) ──
 * Category-agnostic spine, interpolated with the business name. */
export function buildFirstSevenDays(name: string): string[] {
  return [
    `Day 1 — Duplicate your ${name} Notion CRM and load your first 10 prospects`,
    "Day 2 — Customize the 3 highest-impact Canva templates with your name + colors",
    "Day 3 — Set up your Google Business Profile using the provided checklist",
    "Day 4 — Send your first outreach using the AI Sales Assistant scripts",
    "Day 5 — Publish your first 3 social posts from the marketing pack",
    "Day 6 — Set your pricing with the Bronze/Silver/Gold anchor system",
    "Day 7 — Book your first job and trigger the review-ask sequence",
  ];
}

/* ── Business-specific FAQ builder (schema-eligible) ─────────────── */
export function buildBusinessFaq(
  b: Business,
): Array<{ q: string; a: string }> {
  const cat = getBusinessCategory(b.categorySlug);
  return [
    {
      q: `What's inside the ${b.name} Business-in-a-Box?`,
      a: `${b.blurb} A commercial license is included on every tier. Starter ($${BUNDLE_STARTER_USD}) ships the core Notion CRM, top Canva templates, wall art, and a first-7-days checklist. Professional ($${BUNDLE_PROFESSIONAL_USD}) adds the full template pack, the 9-assistant AI Business Pack, Spiker-derived SOPs, and the Crush-the-Competition playbook. Agency ($${BUNDLE_AGENCY_USD}) adds white-label + reseller rights. Ultimate ($${BUNDLE_ULTIMATE_USD}) adds ongoing category updates and cross-business assets.`,
    },
    {
      q: `Do I need experience to start a ${b.name.toLowerCase()} business with this?`,
      a: `No. Every bundle is built so anyone can start: the Welcome PDF walks you through your first 7 days step by step, the instructional video covers how to start a ${b.name.toLowerCase()} business in 2026, and the AI Business Pack handles sales scripts, follow-ups, and marketing copy for you.`,
    },
    {
      q: "What is the AI Business Pack?",
      a: `Nine pre-configured AI assistants for ${b.persona} — sales, follow-up, review responses, marketing content, estimates, customer service, competitive intel, GPT workflow files, and voice scripts. Copy-paste system prompts that work with ChatGPT or Claude, pre-trained on the business type.`,
    },
    {
      q: "Is this based on a real business?",
      a: `Yes. Brian Spiker operated Spiker Carpet & Tile Care in Galt, California for ${YEARS_OPERATING} years — a Sacramento Valley service company. The operating systems, pricing scripts, and review-stacking process in every bundle come from that real operating history.${cat?.authorityWeight === "high" ? " This category is Brian's home turf — the systems map directly." : ""}`,
    },
    {
      q: "What license do I get?",
      a: `Every Apex business bundle includes a commercial license on every tier from $${BUNDLE_STARTER_USD} up, so you can use the templates, systems, and assets to run your own ${b.name.toLowerCase()} business and for paid client work immediately — no extended-license upgrade to buy. Agency adds white-label + reseller rights to rebrand and resell. Full terms are on the license page.`,
    },
    {
      q: "Can I get every business instead of just one?",
      a: "Each Business-in-a-Box is its own one-time purchase you keep. Apex Pass members ($99/yr) get 30% off the Starter and Professional kits; the Agency and Ultimate tiers include done-for-you and live work, so they are full price.",
    },
  ];
}
